import React, { useState } from "react";
import {
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Modal,
  Box,
  Typography,
} from "@mui/material";
import axios from "axios";
import 'animate.css';

const apiUrl = process.env.REACT_APP_API_RESERVAS;

const App = () => {
  const [dni, setDni] = useState("");
  const [results, setResults] = useState([]);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalDays, setTotalDays] = useState(0);
  const [personInfo, setPersonInfo] = useState({ nombre: "", apellido: "" });
  const [companions, setCompanions] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);


  const calculateTotalDays = (reservations) => {
    return reservations.reduce((total, reservation) => {
      const [start, end] = reservation.days;
      const startDate = new Date(start);
      const endDate = new Date(end);
      const diffTime = Math.abs(endDate - startDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return total + diffDays;
    }, 0);
  };

  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    setHasSearched(false); // Reinicia el estado antes de buscar
    try {
      const response = await axios.get(apiUrl);
      const allReservations = response.data;
      const filteredResults = allReservations.filter((reservation) =>
        reservation.miembros.some((miembro) => miembro.dni.toString() === dni)
      );
  
      if (filteredResults.length > 0) {
        const firstMatch = filteredResults[0].miembros.find(
          (miembro) => miembro.dni.toString() === dni
        );
        setPersonInfo({ nombre: firstMatch.nombre, apellido: firstMatch.apellido });
      } else {
        setPersonInfo({ nombre: "", apellido: "" });
      }
  
      const reservationsMapped = filteredResults.map((reservation) => ({
        id: reservation.id,
        days: [reservation.fecha_entrada, reservation.fecha_salida],
        members: reservation.miembros,
      }));
  
      setResults(reservationsMapped);
      setTotalDays(calculateTotalDays(reservationsMapped));
      setHasSearched(true); // Marca que la búsqueda fue completada
    } catch (err) {
      setError("No se pudieron obtener las reservas. Intente nuevamente.");
      setResults([]);
      setTotalDays(0);
      setPersonInfo({ nombre: "", apellido: "" });
      setHasSearched(true); // Incluso con error, marca que la búsqueda terminó
    } finally {
      setLoading(false);
    }
  };
  

  const handleOpenModal = (reservation) => {
    setSelectedReservation(reservation);
    setCompanions(reservation.members.filter(member => member.dni.toString() !== dni));
  };

  const handleCloseModal = () => {
    
    setSelectedReservation(null);
    setCompanions([]);
  };


  return (
    <div style={{ padding: "20px" }}>
      <Typography variant="h5" gutterBottom>
        Informe de Reservas por DNI
      </Typography>

      <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
        <TextField
          label="Ingrese DNI"
          variant="outlined"
          value={dni}
          onChange={(e) => setDni(e.target.value)}
        />
        <Button variant="contained" onClick={handleSearch} disabled={loading}>
          {loading ? "Buscando..." : "Buscar"}
        </Button>
      </div>

      {error && <Typography color="error">{error}</Typography>}

      {personInfo.nombre && (
        <>
          <Typography variant="h6">Nombre: {personInfo.nombre}</Typography>
          <Typography variant="h6">Apellido: {personInfo.apellido}</Typography>
        </>
      )}

      {results.length > 0 ? (
        <>
          <Typography variant="h6" gutterBottom>
            Total de días reservados: {totalDays}
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>N° de Reserva</TableCell>
                  <TableCell>Días Reservados</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((reservation) => (
                  <TableRow
                    key={reservation.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleOpenModal(reservation)}
                  >
                    <TableCell>{reservation.id}</TableCell>
                    <TableCell>{reservation.days.join(" - ")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        !loading && hasSearched && results.length === 0 && (
          <Typography>No se encontraron reservas para este DNI!</Typography>
        )
      )}
      
      

      <Modal open={!!selectedReservation} onClose={handleCloseModal} position="absolute" top="50%" left="50%" >
        <Box
            className={`animate__animated ${
                handleOpenModal ? "animate__backInDown" : "animate__fadeOutUp"
          }`}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)", // CENTRAR correctamente
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: "8px",
            minWidth: "400px", // Ajusta el tamaño mínimo si es necesario
            maxWidth: "90vw",  // Evita que se salga de la pantalla
            textAlign: "center",
          }}
        >
          {selectedReservation && (
            <>
              <Typography variant="h6" gutterBottom>
                Detalles de la Reserva
              </Typography>
              <Typography>
                <strong>Número de Reserva:</strong> {selectedReservation.id}
              </Typography>
              <Typography>
                <strong>Días Reservados:</strong> {selectedReservation.days.join(" - ")}
              </Typography>
              <Typography variant="h6" gutterBottom>
                <strong>Personas que compartieron la reserva:</strong>
              </Typography>
              {companions.length > 0 ? (
                companions.map((member) => (
                  <Typography key={member.id}>{member.dni} - {member.nombre} - {member.apellido} - {member.rol}</Typography>
                ))
              ) : (
                <Typography>No hay otras personas en esta reserva.</Typography>
              )}
            </>
          )}
          <Button variant="contained" color="success" style={{ marginTop: "20px" }} onClick={handleCloseModal}>Cerrar</Button>
        </Box>
      </Modal>
    </div>
  );
};

export default App;
